import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, CheckCircle2, Loader2, Mail } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { api, isSuccessResponse } from "lib/api";
import { apiURl } from "store/actions";
import { validateEmail } from "lib/validation";
import swtAlert from "lib/sweetAlert";

const formRowClass = "grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [serverMessage, setServerMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");

    setIsLoading(true);
    try {
      const resp = await api.post(
        apiURl.forgotPassword,
        { email: email.trim() },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't send reset link";
        setError(msg);
        swtAlert.error("Reset Link Failed", msg);
        return;
      }

      setSentTo(email.trim());
      setServerMessage(
        resp?.message ||
          "If that email is registered, a password reset link has been sent."
      );
      swtAlert.success(
        "Reset Link Sent",
        resp?.message || "Reset link sent"
      );
    } catch (err) {
      const msg = err?.message || "Couldn't send reset link";
      setError(msg);
      swtAlert.error("Reset Link Failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot password | Fortify</title>
      </Helmet>

      <div className="container mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
        <h1 className="mb-2 text-center text-2xl font-semibold">
          Forgot Password?
        </h1>
        <p className="mb-8 text-center text-sm text-muted-foreground">
          Enter the email associated with your account and we&apos;ll send you a
          link to reset your password.
        </p>

        {sentTo ? (
          <ResetLinkSentPanel
            email={sentTo}
            message={serverMessage}
            onResend={() => {
              setSentTo("");
              setServerMessage("");
            }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className={formRowClass}>
              <Label htmlFor="email" className="md:pt-3 md:text-right">
                Email
              </Label>
              <div>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError("");
                  }}
                  aria-invalid={!!error}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                />
                {error && (
                  <p className="mt-1 text-xs text-destructive">{error}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-[260px_1fr]">
              <div />
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full md:w-auto"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Link...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Send Reset Link
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-muted-foreground">
          <Link
            to="/sign-in"
            className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </p>
      </div>
    </>
  );
}

/* eslint-disable react/prop-types -- local presentational helper. */
function ResetLinkSentPanel({ email, message, onResend }) {
  return (
    <div className="space-y-4 rounded-md border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
      <h2 className="text-base font-semibold">Check your email</h2>
      <p>{message}</p>
      <p className="text-xs text-emerald-700 dark:text-emerald-300">
        We sent the link to <span className="font-medium">{email}</span>. The
        link will expire shortly, so use it soon.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        <Button asChild variant="outline" size="sm">
          <Link to="/sign-in">Back to Sign In</Link>
        </Button>
        <Button type="button" size="sm" onClick={onResend}>
          Use a different email
        </Button>
      </div>
    </div>
  );
}
/* eslint-enable react/prop-types */
